// Patient Types (from OpenEMR)
export interface Patient {
  uuid: string
  pid: string
  fname: string
  lname: string
  DOB: string | null
  sex: string | null
  email: string | null
  title: string | null
  street: string | null
  city: string | null
  state: string | null
  postal_code: string | null
  phone_home: string | null
  phone_cell: string | null
  ss: string | null
  pubpid: string | null
}

export interface PatientCreate {
  fname?: string
  lname?: string
  dob?: string
  sex?: string
  email?: string
  title?: string
  street?: string
  city?: string
  state?: string
  postal_code?: string
  phone_home?: string
  use_fake_defaults?: boolean
  sync_to_openemr?: boolean
}

export interface PatientUpdate {
  fname?: string
  lname?: string
  dob?: string
  sex?: string
  email?: string
}

export interface PatientProfileUpdate {
  profile: DiseaseProfile
  severity?: ProfileSeverity
  notes?: string
}

export interface PatientListResponse {
  items: Patient[]
  total: number
  page: number
  page_size: number
}

// Vital Types
export interface VitalRecord {
  id: number
  patient_id: number
  metric: MetricType
  value: number
  unit: string
  timestamp: string
  loinc_code: string | null
  is_pushed_to_openemr: boolean
  push_error: string | null
  job_id: number | null
  created_at: string
}

export interface VitalRecordListResponse {
  items: VitalRecord[]
  total: number
  page: number
  page_size: number
}

export interface GenerateVitalsRequest {
  mode?: GenerationMode
  profile: DiseaseProfile
  metrics: MetricType[]
  start_date: string
  end_date: string
  interval?: string
  seed?: number
  noise_level?: number
  dropout_rate?: number
  push_to_openemr?: boolean
  dry_run?: boolean
  encounter_eid?: string
}

export interface VitalsPushRequest {
  vital_ids?: number[]
  force?: boolean
  encounter_eid?: string
  destination_url?: string
  destination_headers?: { [k: string]: string }
}

export interface VitalsPushResponse {
  total_attempted: number
  successful: number
  failed: number
  errors: Array<{ vital_id: number; error: string }>
}

// Schedule Types
export interface Schedule {
  id: number
  openemr_patient_uuid: string
  name: string
  interval: string
  window_days: number
  profile: DiseaseProfile
  metrics: MetricType[]
  seed: number | null
  noise_level: number
  dropout_rate: number
  push_to_openemr: boolean
  status: ScheduleStatus
  destination_url: string
  apscheduler_job_id: string | null
  last_run_at: string | null
  next_run_at: string | null
  run_count: number
  error_count: number
  last_error: string | null
  created_at: string
  updated_at: string
}

export interface ScheduleCreate {
  openemr_patient_uuid: string
  name: string
  interval: string
  window_days?: number
  profile: DiseaseProfile
  metrics: MetricType[]
  seed?: number
  noise_level?: number
  dropout_rate?: number
  push_to_openemr?: boolean
  destination_url: string
}

export interface ScheduleUpdate {
  name?: string
  interval?: string
  window_days?: number
  profile?: DiseaseProfile
  metrics?: MetricType[]
  seed?: number
  noise_level?: number
  dropout_rate?: number
  push_to_openemr?: boolean
  destination_url?: string
  status?: string
}

export interface ScheduleListResponse {
  items: Schedule[]
  total: number
  page: number
  page_size: number
}

export interface ScheduleRun {
  id: number
  schedule_id: number
  schedule_name: string | null
  openemr_patient_uuid: string | null
  started_at: string | null
  completed_at: string | null
  status: string
  vitals_generated: number
  vitals_pushed: number
  vitals_failed: number
  duration_ms: number | null
  error_message: string | null
}

// Job Types
export interface GenerationJob {
  id: number
  patient_id: number
  mode: GenerationMode
  profile: DiseaseProfile
  metrics: MetricType[]
  start_date: string
  end_date: string
  interval: number
  seed: number | null
  noise_level: string
  push_to_openemr: boolean
  dry_run: boolean
  status: JobStatus
  vitals_generated: number
  vitals_pushed: number
  vitals_failed: number
  error_message: string | null
  started_at: string | null
  completed_at: string | null
  created_at: string
}

export interface JobListResponse {
  items: GenerationJob[]
  total: number
  page: number
  page_size: number
}

// Enums
export type DiseaseProfile = 'normal' | 'copd' | 'hypertension' | 'diabetes'
export type ProfileSeverity = 'mild' | 'moderate' | 'severe'
export type MetricType = 'sbp' | 'dbp' | 'hr' | 'spo2' | 'rr' | 'temp' | 'glucose'
export type GenerationMode = 'profile' | 'synthea_replay'
export type JobStatus = 'pending' | 'running' | 'completed' | 'failed'
export type ScheduleStatus = 'active' | 'paused' | 'disabled'

// Enum display values
export const DISEASE_PROFILES: Array<{ value: DiseaseProfile; label: string }> = [
  { value: 'normal', label: 'Normal' },
  { value: 'copd', label: 'COPD' },
  { value: 'hypertension', label: 'Hypertension' },
  { value: 'diabetes', label: 'Diabetes' },
]

export const PROFILE_SEVERITIES: Array<{ value: ProfileSeverity; label: string }> = [
  { value: 'mild', label: 'Mild' },
  { value: 'moderate', label: 'Moderate' },
  { value: 'severe', label: 'Severe' },
]

export const METRIC_TYPES: Array<{ value: MetricType; label: string; unit: string }> = [
  { value: 'sbp', label: 'Systolic BP', unit: 'mmHg' },
  { value: 'dbp', label: 'Diastolic BP', unit: 'mmHg' },
  { value: 'hr', label: 'Heart Rate', unit: 'bpm' },
  { value: 'spo2', label: 'SpO2', unit: '%' },
  { value: 'rr', label: 'Respiratory Rate', unit: 'breaths/min' },
  { value: 'temp', label: 'Temperature', unit: '°F' },
  { value: 'glucose', label: 'Glucose', unit: 'mg/dL' },
]

export const JOB_STATUSES: Array<{ value: JobStatus; label: string; color: string }> = [
  { value: 'pending', label: 'Pending', color: 'warning' },
  { value: 'running', label: 'Running', color: 'info' },
  { value: 'completed', label: 'Completed', color: 'success' },
  { value: 'failed', label: 'Failed', color: 'error' },
]

// Health check response
export interface HealthResponse {
  status: string
  service: string
  version: string
  timestamp: string
}

export interface ConfigResponse {
  project_name: string
  version: string
  openemr_base_url: string
  schedule_timezone: string
  local_dev_only: boolean
  debug: boolean
}

export interface OpenEMRStatusResponse {
  status: string
  base_url: string
  authenticated: boolean
  error?: string
}
